import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/apiService';

interface QrStatsRange {
  from: string;
  to: string;
}

export interface QrStatsResponse {
  range?: QrStatsRange;
  total: number;
  last_7_days: number;
  by_day: { date: string; count: number }[];
  by_country: { country_code: string | null; count: number }[];
  recent: {
    ip: string | null;
    location: string | null;
    country_code: string | null;
    city: string | null;
    user_agent: string | null;
    referer: string | null;
    created_at: string;
  }[];
}

interface Props {
  qrId: number;
  qrName: string;
  qrCreatedAt: string;
  onClose: () => void;
}

// Preset date ranges + custom. Custom uses two <input type="date"> fields —
// avoids pulling in the date-picker package just for this and matches the
// simpler pattern already used elsewhere in admin pages.
type PresetKey = '7d' | '30d' | '90d' | 'all' | 'custom';

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom…' },
];

const RECENT_PAGE = 20;

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function computeRange(preset: PresetKey, qrCreatedAt: string, custom: { from: string; to: string }): { from: string; to: string } {
  const now = new Date();
  const to = toISODate(now);
  if (preset === 'custom') {
    return { from: custom.from, to: custom.to };
  }
  if (preset === 'all') {
    return { from: qrCreatedAt.slice(0, 10), to };
  }
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  const start = new Date(now);
  start.setDate(start.getDate() - days + 1);
  return { from: toISODate(start), to };
}

const QrStatsModal: React.FC<Props> = ({ qrId, qrName, qrCreatedAt, onClose }) => {
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [custom, setCustom] = useState({
    from: toISODate(new Date(Date.now() - 30 * 86400_000)),
    to: toISODate(new Date()),
  });
  const [stats, setStats] = useState<QrStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentPage, setRecentPage] = useState(0);

  const range = useMemo(() => computeRange(preset, qrCreatedAt, custom), [preset, qrCreatedAt, custom]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRecentPage(0);
    apiService
      .get<QrStatsResponse>(`/api/qrcodes/${qrId}/stats`, {
        params: { from: `${range.from}T00:00:00`, to: `${range.to}T23:59:59`, limit: 500 },
      })
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [qrId, range.from, range.to]);

  const uniqueIps = useMemo(() => {
    if (!stats) return 0;
    return new Set(stats.recent.map((r) => r.ip).filter(Boolean)).size;
  }, [stats]);

  const chartMax = useMemo(() => {
    if (!stats) return 1;
    return Math.max(...stats.by_day.map((d) => d.count), 1);
  }, [stats]);

  const recentPageRows = useMemo(() => {
    if (!stats) return [];
    const start = recentPage * RECENT_PAGE;
    return stats.recent.slice(start, start + RECENT_PAGE);
  }, [stats, recentPage]);

  const totalPages = stats ? Math.max(1, Math.ceil(stats.recent.length / RECENT_PAGE)) : 1;

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 12, color: '#5b6573' }}>QR stats</div>
            <h2 style={{ margin: 0 }}>{qrName}</h2>
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e5ec', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              style={preset === p.key ? presetBtnActive : presetBtn}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <>
              <input
                type="date"
                value={custom.from}
                onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                style={dateInput}
              />
              <span style={{ color: '#5b6573' }}>to</span>
              <input
                type="date"
                value={custom.to}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                style={dateInput}
              />
            </>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#5b6573' }}>
            {range.from} — {range.to}
          </span>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {loading && <p>Loading…</p>}
          {error && (
            <div style={{ background: '#fde7e9', color: '#b30021', padding: 10, borderRadius: 6 }}>{error}</div>
          )}
          {!loading && stats && (
            <>
              <div style={tiles}>
                <Tile label="Total scans" value={stats.total.toLocaleString()} />
                <Tile label="Last 7 days" value={stats.last_7_days.toLocaleString()} />
                <Tile label="Unique IPs (in view)" value={uniqueIps.toLocaleString()} />
                <Tile label="Countries" value={stats.by_country.filter((c) => c.country_code).length.toLocaleString()} />
              </div>

              <Section title="Scans per day">
                {stats.by_day.length === 0 ? (
                  <Empty text="No scans in this range." />
                ) : (
                  <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 120, borderBottom: '1px solid #e2e5ec' }}>
                    {stats.by_day.map((d) => (
                      <div
                        key={d.date}
                        title={`${d.date}: ${d.count}`}
                        style={{
                          flex: 1,
                          minWidth: 4,
                          height: `${(d.count / chartMax) * 100}%`,
                          background: '#0d6efd',
                          opacity: 0.85,
                          borderRadius: '2px 2px 0 0',
                        }}
                      />
                    ))}
                  </div>
                )}
                {stats.by_day.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#5b6573', marginTop: 4 }}>
                    <span>{stats.by_day[0].date}</span>
                    <span>{stats.by_day[stats.by_day.length - 1].date}</span>
                  </div>
                )}
              </Section>

              <Section title="By country">
                {stats.by_country.length === 0 ? (
                  <Empty text="No location data yet." />
                ) : (
                  <table style={tbl}>
                    <thead>
                      <tr><th style={th}>Country</th><th style={{ ...th, textAlign: 'right' }}>Scans</th><th style={{ ...th, width: '40%' }}>&nbsp;</th></tr>
                    </thead>
                    <tbody>
                      {stats.by_country.map((c, i) => {
                        const max = stats.by_country[0]?.count || 1;
                        return (
                          <tr key={i} style={rowBorder}>
                            <td style={td}>{c.country_code || <span style={{ color: '#9ca3ad' }}>unknown</span>}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{c.count.toLocaleString()}</td>
                            <td style={td}>
                              <div style={{ height: 8, background: '#e2e5ec', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${(c.count / max) * 100}%`, height: '100%', background: '#0d6efd' }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </Section>

              <Section title={`Recent scans (${stats.recent.length})`}>
                {stats.recent.length === 0 ? (
                  <Empty text="No scans in this range." />
                ) : (
                  <>
                    <table style={tbl}>
                      <thead>
                        <tr>
                          <th style={th}>When</th>
                          <th style={th}>Where</th>
                          <th style={th}>IP</th>
                          <th style={th}>User agent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentPageRows.map((r, i) => (
                          <tr key={i} style={rowBorder}>
                            <td style={{ ...td, whiteSpace: 'nowrap', color: '#5b6573' }}>{new Date(r.created_at).toLocaleString()}</td>
                            <td style={td}>{r.location || <span style={{ color: '#9ca3ad' }}>—</span>}</td>
                            <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.ip || '—'}</td>
                            <td style={{ ...td, fontSize: 11, color: '#5b6573', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.user_agent || ''}>
                              {r.user_agent || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, fontSize: 13 }}>
                        <button style={presetBtn} onClick={() => setRecentPage((p) => Math.max(0, p - 1))} disabled={recentPage === 0}>‹ Prev</button>
                        <span style={{ padding: '6px 8px', color: '#5b6573' }}>Page {recentPage + 1} of {totalPages}</span>
                        <button style={presetBtn} onClick={() => setRecentPage((p) => Math.min(totalPages - 1, p + 1))} disabled={recentPage >= totalPages - 1}>Next ›</button>
                      </div>
                    )}
                  </>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ margin: '20px 0' }}>
    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>{title}</div>
    {children}
  </div>
);

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ background: '#f6f7fb', borderRadius: 6, padding: 12 }}>
    <div style={{ fontSize: 12, color: '#5b6573' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{value}</div>
  </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ padding: 16, background: '#f6f7fb', borderRadius: 6, color: '#5b6573', fontSize: 13, textAlign: 'center' }}>{text}</div>
);

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,26,43,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const modal: React.CSSProperties = {
  background: 'white', borderRadius: 10, width: '100%', maxWidth: 900, maxHeight: '90vh',
  display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,.25)',
};
const header: React.CSSProperties = {
  padding: '16px 20px', borderBottom: '1px solid #e2e5ec',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const closeBtn: React.CSSProperties = {
  background: 'none', border: 0, fontSize: 28, color: '#5b6573', cursor: 'pointer', lineHeight: 1,
};
const presetBtn: React.CSSProperties = {
  background: 'white', color: '#3a4252', border: '1px solid #cdd3dc',
  padding: '6px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
};
const presetBtnActive: React.CSSProperties = {
  ...presetBtn, background: '#0f1a2b', color: 'white', border: '1px solid #0f1a2b',
};
const dateInput: React.CSSProperties = {
  padding: '5px 8px', border: '1px solid #cdd3dc', borderRadius: 6, fontSize: 13,
};
const tiles: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 };
const tbl: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#5b6573', fontWeight: 600, borderBottom: '1px solid #e2e5ec' };
const td: React.CSSProperties = { padding: '6px 8px' };
const rowBorder: React.CSSProperties = { borderBottom: '1px solid #f0f1f4' };

export default QrStatsModal;
